import { useMemo } from 'react';
import { useLocationStore, prayerConfigFromSettings, voluntaryOverridesFromSettings } from '../stores/locationStore';
import { computeDay, type DayCycle } from '../utils/dayCycle';
import { formatDateToLocal } from '../utils/date';

/**
 * Resolve the solar day-cycle (+ prayers, when enabled) for a given calendar
 * day at the user's saved location. Returns null when no location is set — so
 * the clock renders exactly as before until the feature is configured.
 *
 * Memoised on the calendar day (not the Date identity) + the location/prayer
 * settings, and anchored at noon-UTC of that day so a far-flung location's
 * timezone never shifts which calendar date we compute.
 */
export function useDayCycle(date: Date): DayCycle | null {
  const location = useLocationStore((s) => s.location);
  const prayer = useLocationStore((s) => s.prayer);
  const dateKey = formatDateToLocal(date);

  return useMemo(() => {
    if (!location) return null;
    try {
      return computeDay({
        location,
        date: new Date(`${dateKey}T12:00:00Z`),
        prayer: prayer.enabled,
        ramadan: prayer.ramadan,
        fajrAngle: prayer.fajrAngle,
        prayerConfig: prayerConfigFromSettings(prayer),
        duha: prayer.enabled && prayer.duha,
        qiyam: prayer.enabled && prayer.qiyam,
        duhaDurationMin: prayer.duhaDurationMin,
        qiyamDurationMin: prayer.qiyamDurationMin,
        ...voluntaryOverridesFromSettings(prayer),
      });
    } catch {
      return null;
    }
  }, [location, prayer, dateKey]);
}
